import React from "react";
import styled, { css } from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";

export interface TableSelectRowProps {
  name: string;
  /** `null` when no real count has ever been computed for this row —
   *  rendered as "-" rather than a fabricated number. */
  rowCount: number | null;
  columnCount: number | null;
  /** True when the user has ticked the checkbox to import this table. */
  checked?: boolean;
  /** True when the row is the current table-preview target (light blue tint). */
  highlighted?: boolean;
  onToggleCheck?: (next: boolean) => void;
  /** Click on the row body (anywhere outside the checkbox) — drives preview. */
  onRowClick?: () => void;
}

/**
 * Grid layout — 4 columns: [checkbox][name (flex)][rows meta][cols meta].
 *
 * Fixed-width meta columns keep "1,200 rows / 890 rows / 450 rows" stacked
 * in a clean column instead of floating to the right edge based on text
 * width. `align-items: center` keeps the checkbox vertically aligned with
 * the text regardless of line-height differences between body and caption.
 */
const Row = styled.div<{ $highlighted: boolean }>`
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) 110px 110px;
  align-items: center;
  gap: ${spacing[4]};
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${rawColors.stroke[200]};
  cursor: pointer;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  background-color: ${({ $highlighted }) =>
    $highlighted ? rawColors.primary[100] : rawColors.structural.white};

  ${({ $highlighted }) =>
    !$highlighted &&
    css`
      &:hover {
        background-color: ${rawColors.stroke[100]};
      }
    `}

  &:last-of-type {
    border-bottom: none;
  }
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: ${rawColors.primary[500]};
  cursor: pointer;
`;

const Name = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  text-align: center;
`;

export const TableSelectRow: React.FC<TableSelectRowProps> = ({
  name,
  rowCount,
  columnCount,
  checked = false,
  highlighted = false,
  onToggleCheck,
  onRowClick,
}) => (
  <Row $highlighted={highlighted} onClick={onRowClick}>
    <Checkbox
      checked={checked}
      onChange={(e) => onToggleCheck?.(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      aria-label={`Select ${name}`}
    />
    <Name>{name}</Name>
    <Meta>{rowCount != null ? `${rowCount.toLocaleString()} rows` : "—"}</Meta>
    <Meta>{columnCount != null ? `${columnCount} columns` : "—"}</Meta>
  </Row>
);

export default TableSelectRow;
